import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { cn } from '@/lib/utils'
import { useTeamMembers, type TeamMember } from '@/api/agents'
import { useProfile } from '@/api/profile'
import { useNonLusCollegues } from '@/api/messagesCollegues'
import { ouvrirCollegue } from '@/lib/ouvrirConversation'
import { useUIStore } from '@/stores/useUIStore'

/**
 * Collègues dans la barre de gauche (modèle Quo « Direct messages ») :
 * qui est là, un clic pour ouvrir le fil de messages avec lui (l'appel se
 * fait depuis l'en-tête du fil). Remplace la page Équipe du téléphone ; la
 * gestion des membres reste dans Réglages > Équipe.
 */
export function Collegues({ darkMode, collapsed, actif }: { darkMode: boolean; collapsed: boolean; actif: boolean }) {
  const { t } = useTranslation()
  const { data: profile } = useProfile()
  const { data: membres = [] } = useTeamMembers()
  const ouvert = useUIStore((s) => s.selectedCollegueId)

  const autres = useMemo(
    () => membres.filter((m: TeamMember) => m.user_id !== profile?.id),
    [membres, profile?.id],
  )
  const nonLus = useNonLusCollegues(autres.length > 0)
  if (!autres.length) return null

  const nom = (m: TeamMember) => `${m.user_first_name || ''} ${m.user_last_name || ''}`.trim() || m.user_email

  return (
    <div className={cn('px-2 pt-2 border-t', darkMode ? 'border-slate-800' : 'border-slate-200')}>
      {!collapsed && (
        <p className={cn('px-3 pt-1 pb-0.5 text-[10.5px] font-semibold uppercase tracking-wider', darkMode ? 'text-slate-500' : 'text-slate-400')}>
          {t('sidebar.colleagues', 'Collègues')}
        </p>
      )}
      <div className="max-h-40 overflow-y-auto space-y-px">
        {autres.map((m) => {
          const dispo = m.status === 'ONLINE' && !m.dnd_enabled
          const occupe = m.status === 'BUSY'
          const n = nonLus[m.user_id] ?? 0
          const selectionne = actif && ouvert === m.user_id
          return (
            <button
              key={m.id}
              type="button"
              onClick={() => ouvrirCollegue(m.user_id)}
              title={t('messagesCollegues.ecrireA', 'Écrire à {{name}}', { name: nom(m) })}
              className={cn(
                'group w-full flex items-center h-7 rounded-md text-[13px] transition-colors',
                collapsed ? 'justify-center px-2' : 'gap-2.5 px-3',
                selectionne
                  ? darkMode ? 'bg-slate-800 text-white' : 'bg-slate-100 text-slate-900'
                  : dispo || occupe || n > 0
                    ? darkMode ? 'text-slate-300 hover:bg-slate-800 hover:text-white' : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'
                    : darkMode ? 'text-slate-500 hover:bg-slate-800 hover:text-slate-300' : 'text-slate-400 hover:bg-slate-50 hover:text-slate-600',
                n > 0 && 'font-semibold',
              )}
            >
              <span className="relative flex-shrink-0">
                <span className={cn('flex h-5 w-5 items-center justify-center rounded-full text-[10px] font-semibold', darkMode ? 'bg-slate-700 text-slate-200' : 'bg-slate-200 text-slate-700')}>
                  {(m.user_first_name?.[0] || m.user_email[0] || '?').toUpperCase()}
                </span>
                <span className={cn('absolute -bottom-0.5 -right-0.5 h-2 w-2 rounded-full ring-1', darkMode ? 'ring-slate-900' : 'ring-white', dispo ? 'bg-emerald-500' : occupe ? 'bg-amber-500' : 'bg-slate-400')} />
                {/* Non lus, barre repliée */}
                {collapsed && n > 0 && (
                  <span className="absolute -top-1 -right-1.5 min-w-[14px] h-3.5 px-1 flex items-center justify-center text-[8px] font-bold bg-indigo-600 text-white rounded-full">
                    {n > 9 ? '9+' : n}
                  </span>
                )}
              </span>
              {!collapsed && (
                <>
                  <span className="flex-1 text-left truncate">{nom(m)}</span>
                  {n > 0 && (
                    <span className="px-1.5 py-0.5 text-[10px] leading-none font-bold bg-indigo-600 text-white rounded-full">
                      {n}
                    </span>
                  )}
                </>
              )}
            </button>
          )
        })}
      </div>
    </div>
  )
}
